import { useState } from "react";
import {
    ArrowUpRight,
    Mail,
    MessageCircle,
    Instagram,
    Music2,
    Youtube,
    Send,
} from "lucide-react";
import { AGENCY } from "@/lib/data";
import { IOSHighlight } from "@/components/site/iOSKit";

export const Footer = () => {
    const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });

    const handleSubmit = (e) => {
        e.preventDefault();
        const subject = encodeURIComponent(
            `Nuevo brief de ${form.firstName} ${form.lastName}`.trim() ||
                "Nuevo brief",
        );
        const body = encodeURIComponent(
            `Nombre: ${form.firstName} ${form.lastName}\nEmail: ${form.email}\n\nCuéntame del proyecto...`,
        );
        window.location.href = `${AGENCY.emailUrl}?subject=${subject}&body=${body}`;
    };

    const formValid =
        form.firstName.trim() &&
        form.lastName.trim() &&
        /\S+@\S+\.\S+/.test(form.email);

    return (
        <footer
            id="contacto"
            data-testid="footer-section"
            className="relative bg-[#0A0A0A] border-t border-white/5"
        >
            {/* ============ EDITORIAL SECTION: big heading + info card (no image) ============ */}
            <section className="px-6 md:px-12 lg:px-20 pt-24 md:pt-32 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    {/* Left column: Editorial heading */}
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="w-10 h-px bg-[#FF2A2A]" />
                            <span className="text-[11px] tracking-[0.3em] uppercase text-white/60">
                                Hablemos · Está a un click
                            </span>
                        </div>
                        <h2
                            data-testid="footer-heading"
                            className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-balance"
                        >
                            <span className="font-serif text-white/80">Cotiza</span>{" "}
                            con{" "}
                            <IOSHighlight variant="red">
                                nuestro equipo.
                            </IOSHighlight>
                        </h2>
                        <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md">
                            Te respondemos en minutos. Sin formularios largos,
                            sin promesas vacías. Solo trabajo.
                        </p>
                    </div>

                    {/* Right column: Info card with CTA (no image) */}
                    <div className="lg:col-span-6">
                        <div
                            data-testid="contact-info-card"
                            className="bg-[#141414] border border-white/10 rounded-[26px] p-6 md:p-8 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
                        >
                            {/* Top chrome */}
                            <div className="flex items-center gap-3 pb-6 border-b border-white/10">
                                <div className="w-11 h-11 rounded-[12px] bg-[#FF2A2A] flex items-center justify-center shrink-0">
                                    <MessageCircle className="w-5 h-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/60">
                                        Impacto Visual
                                    </div>
                                    <div className="text-white text-[15px] font-semibold">
                                        Respondemos en menos de 10 min
                                    </div>
                                </div>
                                <span className="flex items-center gap-1.5 text-[10px] tracking-wider text-white/50">
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />
                                    En línea
                                </span>
                            </div>

                            {/* Stats grid */}
                            <div className="grid grid-cols-3 gap-3 py-6 border-b border-white/10">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/40">
                                        WhatsApp
                                    </div>
                                    <div className="text-white text-sm font-semibold mt-1">
                                        Activos Lun-Vie
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/40">
                                        Email
                                    </div>
                                    <div className="text-white text-sm font-semibold mt-1">
                                        &lt; 24h
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/40">
                                        Llamada
                                    </div>
                                    <div className="text-white text-sm font-semibold mt-1">
                                        Lun-Vie
                                    </div>
                                </div>
                            </div>

                            {/* Contact details + CTA button */}
                            <div className="pt-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-white/40">
                                            WhatsApp directo
                                        </div>
                                        <div className="text-white text-lg font-display tracking-tight mt-1">
                                            {AGENCY.whatsapp}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-white/40 text-right">
                                            Email
                                        </div>
                                        <div className="text-white text-sm mt-1">
                                            {AGENCY.email}
                                        </div>
                                    </div>
                                </div>

                                <a
                                    href={AGENCY.whatsappUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="cotiza-aqui-cta"
                                    className="inline-flex items-center gap-2.5 bg-[#FF2A2A] hover:bg-[#CC1E1E] text-white px-6 py-3 rounded-full transition-colors self-start"
                                >
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-4 h-4 fill-white"
                                        aria-hidden="true"
                                    >
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
                                    </svg>
                                    <span className="text-[13px] font-semibold tracking-tight">
                                        Cotiza aquí
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ BIG RED CTA BANNER WITH INLINE FORM ============ */}
            <div
                data-testid="big-cta-form"
                className="mx-6 md:mx-12 lg:mx-20 bg-[#FF2A2A] rounded-[26px] p-8 md:p-10 lg:p-12"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                    <div className="lg:col-span-5">
                        <div className="text-[11px] tracking-[0.3em] uppercase text-white/80 mb-3">
                            Hablemos de tu proyecto
                        </div>
                        <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] text-white text-balance">
                            Cotiza con
                            <br />
                            nuestro equipo.
                        </h3>
                        <p className="mt-4 text-white/85 text-sm md:text-base max-w-sm">
                            Déjanos tus datos y te contactamos en menos de 24h.
                        </p>
                    </div>

                    {/* Inline form - light inputs for visibility */}
                    <form
                        onSubmit={handleSubmit}
                        className="lg:col-span-7 bg-white rounded-2xl p-5 md:p-6"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A]/60 mb-1.5"
                                >
                                    Nombre
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    required
                                    value={form.firstName}
                                    onChange={(e) =>
                                        setForm({ ...form, firstName: e.target.value })
                                    }
                                    placeholder="Tu nombre"
                                    data-testid="form-firstname"
                                    className="w-full bg-[#F3F4F6] border border-black/10 text-[#0A0A0A] placeholder:text-[#0A0A0A]/40 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF2A2A] focus:bg-white transition-colors"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A]/60 mb-1.5"
                                >
                                    Apellido
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    required
                                    value={form.lastName}
                                    onChange={(e) =>
                                        setForm({ ...form, lastName: e.target.value })
                                    }
                                    placeholder="Tu apellido"
                                    data-testid="form-lastname"
                                    className="w-full bg-[#F3F4F6] border border-black/10 text-[#0A0A0A] placeholder:text-[#0A0A0A]/40 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF2A2A] focus:bg-white transition-colors"
                                />
                            </div>
                        </div>

                        <div className="mt-3 md:mt-4">
                            <label
                                htmlFor="email"
                                className="block text-[10px] tracking-[0.2em] uppercase text-[#0A0A0A]/60 mb-1.5"
                            >
                                Correo
                            </label>
                            <input
                                id="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                placeholder="tu@correo.com"
                                data-testid="form-email"
                                className="w-full bg-[#F3F4F6] border border-black/10 text-[#0A0A0A] placeholder:text-[#0A0A0A]/40 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#FF2A2A] focus:bg-white transition-colors"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={!formValid}
                            data-testid="form-submit"
                            className="group mt-4 md:mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-[#0A0A0A] hover:bg-[#FF2A2A] text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0A0A0A] py-3.5 px-5 rounded-xl font-semibold text-sm tracking-wide transition-all duration-300"
                        >
                            <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            Enviar
                        </button>
                        <p className="mt-3 text-[11px] text-[#0A0A0A]/60 text-center">
                            Al enviar aceptas ser contactado por nuestro equipo.
                        </p>
                    </form>
                </div>
            </div>

            {/* ============ SITE MAP / LINK COLUMNS ============ */}
            <div className="px-6 md:px-12 lg:px-20 pt-20 pb-10 border-t border-white/10 mt-14 grid grid-cols-2 md:grid-cols-5 gap-8">
                <div className="col-span-2 md:col-span-2">
                    <a href="#top" className="flex items-center gap-2 mb-4">
                        <span className="w-2 h-2 bg-[#FF2A2A] rounded-full" />
                        <span className="font-display text-lg tracking-[0.2em] uppercase">
                            {AGENCY.name}
                        </span>
                    </a>
                    <p className="text-white/50 text-sm leading-relaxed max-w-xs">
                        Agencia creativa especializada en diseño y producción
                        publicitaria de alto impacto.
                    </p>
                    <div className="flex items-center gap-3 mt-5">
                        {[
                            { Icon: Instagram, href: AGENCY.instagram, label: "instagram" },
                            { Icon: Youtube, href: "#", label: "youtube" },
                            { Icon: Music2, href: "#", label: "tiktok" },
                            { Icon: Mail, href: AGENCY.emailUrl, label: "email" },
                        ].map(({ Icon, href, label }) => (
                            <a
                                key={label}
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`footer-social-${label}`}
                                className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-[#FF2A2A] hover:border-[#FF2A2A] flex items-center justify-center transition-colors"
                            >
                                <Icon className="w-4 h-4 text-white" />
                            </a>
                        ))}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">
                        Agencia
                    </div>
                    <ul className="space-y-2 text-sm">
                        {[
                            { label: "Portafolio", href: "#reel" },
                            { label: "Servicios", href: "#servicios" },
                            { label: "Contacto", href: "#contacto" },
                        ].map((l) => (
                            <li key={l.label}>
                                <a
                                    href={l.href}
                                    className="text-white/70 hover:text-[#FF2A2A] transition-colors"
                                >
                                    {l.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">
                        Horario
                    </div>
                    <ul className="space-y-2 text-sm text-white/70">
                        <li className="flex items-baseline justify-between gap-3">
                            <span>Lun — Vie</span>
                            <span className="text-white font-medium">
                                08:30 — 17:00
                            </span>
                        </li>
                        <li className="flex items-baseline justify-between gap-3">
                            <span>Sábado</span>
                            <span className="text-[#FF2A2A] font-medium">
                                Cerrado
                            </span>
                        </li>
                        <li className="flex items-baseline justify-between gap-3">
                            <span>Domingo</span>
                            <span className="text-[#FF2A2A] font-medium">
                                Cerrado
                            </span>
                        </li>
                        <li className="flex items-center gap-2 pt-2 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#4ade80] animate-pulse" />
                            <span className="text-white/50">
                                WhatsApp activo de Lun-Vie
                            </span>
                        </li>
                    </ul>
                </div>

                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-4">
                        Dirección
                    </div>
                    <address className="not-italic text-sm text-white/70 leading-relaxed">
                        Abraham Lincoln #1670,
                        <br />
                        La Serena
                    </address>
                    <a
                        href="https://maps.google.com/?q=Abraham+Lincoln+1670+La+Serena"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="footer-map-link"
                        className="inline-flex items-center gap-1.5 mt-4 text-xs text-white/60 hover:text-[#FF2A2A] transition-colors"
                    >
                        Ver en mapa
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </a>
                </div>
            </div>

            {/* ============ MASSIVE AGENCY NAME MARQUEE STRIP ============ */}
            <div className="overflow-hidden border-t border-white/10 py-6">
                <div className="marquee-track">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <span
                            key={i}
                            className="font-display text-5xl md:text-7xl lg:text-8xl px-8 flex items-center gap-8 text-white/10 hover:text-[#FF2A2A] transition-colors whitespace-nowrap tracking-tight"
                        >
                            {AGENCY.name}
                            <span className="w-3 h-3 rounded-full bg-[#FF2A2A]/50" />
                        </span>
                    ))}
                </div>
            </div>

            {/* ============ FINE PRINT ============ */}
            <div className="px-6 md:px-12 lg:px-20 py-6 border-t border-white/10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between text-[11px] tracking-[0.2em] uppercase text-white/40">
                <span>
                    © {new Date().getFullYear()} {AGENCY.name} — All rights reserved
                </span>
                <div className="flex items-center gap-6">
                    <span>Llegamos a todo el norte de Chile</span>
                    <a
                        href="#top"
                        data-testid="footer-backtop-link"
                        className="hover:text-[#FF2A2A] transition-colors"
                    >
                        ↑ Volver arriba
                    </a>
                </div>
            </div>
        </footer>
    );
};
